import { useEffect, useState, useCallback } from "react";
import { Users, Check } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";

type Invite = {
  id: string;
  email: string;
  role: 'admin' | 'user';
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invited_at: string | null;
  expires_at: string | null;
  company_id: string;
  company_name_snapshot?: string | null;
  invited_by_email?: string | null;
  invited_by_name?: string | null;
  customers?: { company_name?: string | null } | null;
};

export default function InvitesPage() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [showAcceptAlert, setShowAcceptAlert] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null);
  const [accepting, setAccepting] = useState(false);

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);

      // SECURITY: Use backend API instead of direct database query
      const response = await apiClient.get<{
        ok: boolean;
        invites: Invite[];
      }>('/user/invites');

      if (!response.ok) {
        toast({
          title: "Error",
          description: "Failed to load invites",
          variant: "destructive",
        });
        setInvites([]);
      } else {
        setInvites(response.invites || []);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message ?? 'Failed to load invites',
        variant: "destructive",
      });
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const acceptInvite = async () => {
    if (!selectedInvite) return;

    setAccepting(true);
    try {
      console.log('[InvitesPage] Accepting invite:', selectedInvite.id);

      // Get auth token for backend request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const SIGNALING_SERVER_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';

      // Call backend API
      const response = await fetch(`${SIGNALING_SERVER_URL}/api/team/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          mode: 'in_app',
          invite_id: selectedInvite.id,
          transfer: true,
        }),
      });

      const data = await response.json();
      console.log('[InvitesPage] Accept invite response:', { status: response.status, data });

      if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      toast({
        title: "Success",
        description: "Invite accepted. You've joined the team.",
      });

      setShowAcceptAlert(false);
      setSelectedInvite(null);
      fetchInvites();
    } catch (error: any) {
      console.error('[InvitesPage] Accept invite error:', error);
      toast({
        title: "Error",
        description: error.message ?? 'Failed to accept invite',
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleAcceptClick = (invite: Invite) => {
    setSelectedInvite(invite);
    setShowAcceptAlert(true);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Team Invites</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage invitations to join other teams
        </p>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading invites...</p>
        </div>
      ) : invites.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Users className="h-16 w-16 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900">No Invites</p>
            <p className="text-sm text-muted-foreground mt-1">
              You'll see them here when someone invites you
            </p>
          </div>
        </div>
      ) : (
        /* Invites List */
        <div className="space-y-4">
          {invites.map((invite) => {
            const isPending = invite.status === 'pending';
            const companyName =
              invite.company_name_snapshot ??
              invite.customers?.company_name ??
              '—';
            const invitedBy =
              invite.invited_by_name ??
              invite.invited_by_email ??
              '—';

            return (
              <Card key={invite.id}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {companyName}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Invited by: {invitedBy}
                      </p>
                    </div>
                    <Badge variant={isPending ? 'default' : 'secondary'}>
                      {invite.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-muted-foreground">Role</p>
                      <p className="font-medium capitalize">{invite.role}</p>
                    </div>
                    {invite.invited_at && (
                      <div>
                        <p className="text-muted-foreground">Invited</p>
                        <p className="font-medium">
                          {new Date(invite.invited_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {invite.expires_at && (
                      <div>
                        <p className="text-muted-foreground">Expires</p>
                        <p className="font-medium">
                          {new Date(invite.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {isPending && (
                    <Button
                      onClick={() => handleAcceptClick(invite)}
                      className="w-full"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Accept Invitation
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Accept Confirmation Alert */}
      <AlertDialog open={showAcceptAlert} onOpenChange={setShowAcceptAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to accept the invitation from{' '}
              {selectedInvite?.company_name_snapshot ||
                selectedInvite?.customers?.company_name ||
                'this company'}
              ?
              {'\n\n'}
              You'll join their team as a{' '}
              {selectedInvite?.role === 'admin' ? 'administrator' : 'user'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={accepting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={acceptInvite} disabled={accepting}>
              {accepting ? "Accepting..." : "Accept"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
